import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { Nullable } from '@client/core/data/types';
import { Pagination } from '@client/core/http/pagination';
import { HttpResponse, HttpResponseWithMeta, PaginationResponse, SearchRequestParams } from '@client/core/http/types';
import {
  ConstructionMaterial,
  EditableConstructionMaterial,
} from '@client/features/material-catalog/models/construction-material';
import { ConstructionMaterialCategory } from '@client/features/material-catalog/models/construction-material-category';
import { ConstructionMaterialSupplier } from '@client/features/material-catalog/models/construction-material-supplier';

export interface FindAllMaterialsParams extends SearchRequestParams {
  category?: Nullable<ConstructionMaterialCategory['slug']>;
  supplier?: Nullable<ConstructionMaterialSupplier['id']>;
}

export class ConstructionMaterialDataSource extends DataSource {
  async findAll(ownerId: number, params?: FindAllMaterialsParams): Promise<PaginationResponse<ConstructionMaterial[]>> {
    const response = await this.httpClient.get<HttpResponseWithMeta<ConstructionMaterial[], Pagination>>(
      `/workspace/${ownerId}/construction-materials`,
      {
        params: {
          ...params,
          page: params?.page ?? 1,
        },
      },
    );

    return {
      data: response.data.data.map((json) => fromJson(ConstructionMaterial, json)),
      pagination: fromJson(Pagination, response.data.meta),
    };
  }

  async create(ownerId: number, data: EditableConstructionMaterial): Promise<ConstructionMaterial> {
    const formData: CreateConstructionMaterialRequest = toCreateConstructionMaterialRequest(data);

    const response = await this.httpClient.post<HttpResponse<ConstructionMaterial>>(
      `/workspace/${ownerId}/construction-materials`,
      formData,
    );

    return fromJson(ConstructionMaterial, response.data.data);
  }

  async update(ownerId: number, data: EditableConstructionMaterial): Promise<ConstructionMaterial> {
    const formData: CreateConstructionMaterialRequest = toCreateConstructionMaterialRequest(data);

    const response = await this.httpClient.patch<HttpResponse<ConstructionMaterial>>(
      `/workspace/${ownerId}/construction-materials/${data.id}`,
      formData,
    );

    return fromJson(ConstructionMaterial, response.data.data);
  }

  async destroy(ownerId: number, material: ConstructionMaterial): Promise<void> {
    await this.httpClient.delete(`/workspace/${ownerId}/construction-materials/${material.id}`);
  }
}

function toCreateConstructionMaterialRequest(data: EditableConstructionMaterial): CreateConstructionMaterialRequest {
  const formData: CreateConstructionMaterialRequest = {
    ...data,
    internet_sku: data.internetSku,
    item_sku: data.itemSku,
    image_url: data.imageUrl,
    construction_material_category: data.category,
  };

  if (data.supplierId === null && data.supplierName === null) {
    formData.construction_material_supplier_id = null;
  } else if (data.supplierId) {
    formData.construction_material_supplier_id = data.supplierId;
  } else if (data.supplierName) {
    formData.construction_material_supplier_name = data.supplierName;
  }

  return formData;
}

interface CreateConstructionMaterialRequest {
  id?: string;
  construction_material_supplier_id?: Nullable<string>;
  construction_material_supplier_name?: Nullable<string>;
  construction_material_category?: Nullable<string>;
  description: string;
  price: Nullable<number>;
  unit: Nullable<string>;
  url: Nullable<string>;
  item_sku: Nullable<string>;
  internet_sku: Nullable<string>;
  notes: Nullable<string>;
  image_url: Nullable<string>;
}
